import * as React from "react";
import { clampSlide, slideFromScroll, slidesToMount } from "../lib/mediaCard";

export type MediaCardStepMethod = "button" | "swipe";

/**
 * An image-first card: a media box (one image, or a swipeable carousel) with a
 * borderless title and metadata bar under it. It knows nothing about museum
 * objects, so anything that leads with a picture (an artifact, a tool, an
 * Are.na block) can use it.
 *
 * Layout and theme come from the `su-*` classes in styles.css; put cards in a
 * `.su-card-grid` and they sit flush, divided by hairlines. The same classes
 * work from plain HTML.
 */
export function MediaCard({
  slideCount,
  renderSlide,
  emptyMedia,
  title,
  meta,
  children,
  badge,
  onOpen,
  current = false,
  ratio,
  resetKey,
  onSlideChange,
}: {
  slideCount: number;
  /** Renders one slide. Only the current slide and its neighbours are mounted. */
  renderSlide: (index: number) => React.ReactNode;
  /** Shown in the media box when there are no slides. */
  emptyMedia?: React.ReactNode;
  title: React.ReactNode;
  /** One mono line under the title; it truncates rather than wraps. */
  meta?: React.ReactNode;
  /** Further rows, e.g. details in a roomier view. */
  children?: React.ReactNode;
  /** Pinned to the media box's top-right corner. */
  badge?: React.ReactNode;
  onOpen?: () => void;
  /** Marks the card as the one currently open. */
  current?: boolean;
  /** CSS aspect ratio of the media box; the grid's size class sets the default. */
  ratio?: string;
  /** Changing it returns the carousel to the first slide. */
  resetKey?: string;
  onSlideChange?: (index: number, method: MediaCardStepMethod) => void;
}) {
  const [index, setIndex] = React.useState(0);
  const trackRef = React.useRef<HTMLDivElement>(null);
  // Set while a button-driven scroll animates, so the scroll events it causes
  // are not reported as swipes.
  const settlingTo = React.useRef<number | null>(null);

  React.useEffect(() => {
    setIndex(0);
    settlingTo.current = null;
    trackRef.current?.scrollTo({ left: 0 });
  }, [resetKey]);

  const step = (delta: number) => {
    const target = clampSlide(index + delta, slideCount);
    const track = trackRef.current;
    if (target === index || !track) return;
    settlingTo.current = target;
    // An interrupted smooth scroll never lands on the target, so stop
    // waiting for it after the animation would have finished.
    window.setTimeout(() => {
      if (settlingTo.current === target) settlingTo.current = null;
    }, 700);
    track.scrollTo({ left: target * track.clientWidth, behavior: "smooth" });
    setIndex(target);
    onSlideChange?.(target, "button");
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const settled = slideFromScroll(track.scrollLeft, track.clientWidth, slideCount);
    if (settlingTo.current !== null) {
      if (settled === settlingTo.current) settlingTo.current = null;
      return;
    }
    if (settled !== index) {
      setIndex(settled);
      onSlideChange?.(settled, "swipe");
    }
  };

  const mounted = slidesToMount(index, slideCount);

  return (
    <article
      className="su-media-card"
      aria-current={current ? "true" : undefined}
      onClick={onOpen}
    >
      <div className="su-media" style={ratio ? { aspectRatio: ratio } : undefined}>
        {slideCount > 0 ? (
          <div className="su-media-track" ref={trackRef} onScroll={handleScroll}>
            {Array.from({ length: slideCount }, (_, i) => (
              <div key={i} className="su-media-slide" aria-hidden={i !== index}>
                {mounted.has(i) ? renderSlide(i) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="su-media-empty">{emptyMedia}</div>
        )}
        {slideCount > 1 && (
          <>
            <button
              type="button"
              className="su-media-step su-media-step--prev"
              aria-label="Previous image"
              disabled={index === 0}
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
            >
              <Chevron direction="left" />
            </button>
            <button
              type="button"
              className="su-media-step su-media-step--next"
              aria-label="Next image"
              disabled={index === slideCount - 1}
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
            >
              <Chevron direction="right" />
            </button>
            <span className="su-media-count" aria-live="polite">
              {index + 1}/{slideCount}
            </span>
          </>
        )}
        {badge && <div className="su-card-badge">{badge}</div>}
      </div>
      <div className="su-card-body">
        {onOpen ? (
          <button
            type="button"
            className="su-card-title"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            {title}
          </button>
        ) : (
          <span className="su-card-title">{title}</span>
        )}
        {meta && <div className="su-card-meta">{meta}</div>}
        {children}
      </div>
    </article>
  );
}

const Chevron = ({ direction }: { direction: "left" | "right" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={direction === "left" ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} />
  </svg>
);
